<template>
  <div v-if="visible" class="village-overlay">
    <v-btn icon class="village-close-btn" @click="$emit('close')">
      <v-icon color="#57f1ff">mdi-close</v-icon>
    </v-btn>

    <div v-if="!loading" class="village-map-summary">
      <div class="summary-title">{{ village.name || village.code || '村级地块' }}</div>
      <div class="summary-meta">
        <span>当前展示 GeoServer WMS 地块图层</span>
        <span>点击地块查看详细信息</span>
      </div>
    </div>

    <div v-if="!loading" class="plot-side-panel">
      <div class="plot-legend-card">
        <div class="plot-legend-title">地类图例</div>
        <div class="plot-legend-table">
          <div class="plot-legend-head">
            <span>一级分类</span>
            <span>二级分类</span>
          </div>
          <div v-for="group in legendGroups" :key="group.code" class="plot-legend-row">
            <div class="plot-legend-primary">{{ group.label }}</div>
            <div class="plot-legend-secondary">
              <div v-for="item in group.items" :key="item.code" class="plot-legend-item">
                <span class="plot-legend-swatch" :style="{ backgroundColor: item.color }"></span>
                <span class="plot-legend-label">{{ item.code }} {{ item.label }}</span>
              </div>
            </div>
          </div>
        </div>

        <div v-if="isAhicDashboard" class="plot-source-legend">
          <div class="plot-legend-title mb-0">来源类型</div>
          <div class="plot-source-items">
            <div v-for="item in plotSourceLegendItems" :key="item.code" class="plot-legend-item">
              <span class="plot-legend-swatch" :style="item.style"></span>
              <span class="plot-legend-label">{{ item.label }}</span>
            </div>
          </div>
        </div>
      </div>

      <div v-if="selectedVillagePlot" class="plot-info-panel plot-card">
        <div class="plot-info-header">
          <div class="plot-info-title">地块信息</div>
          <v-btn x-small text color="#89fff0" class="plot-info-action" @click="selectVillagePlot(null)">
            收起
          </v-btn>
        </div>
        <div class="plot-info-row"><span>地块编码</span><strong>{{ selectedVillagePlot.plotCode }}</strong></div>
        <div v-if="selectedVillagePlot.plotName" class="plot-info-row"><span>地块名称</span><strong>{{ selectedVillagePlot.plotName }}</strong></div>
        <div class="plot-info-row"><span>{{ isAhicDashboard ? '权利人姓名' : '权属人大名' }}</span><strong>{{ selectedVillagePlot.ownerName }}</strong></div>
        <div class="plot-info-row"><span>{{ isAhicDashboard ? '权利人证件号' : '权属人身份证号' }}</span><strong>{{ selectedVillagePlot.ownerIdCard }}</strong></div>
        <div class="plot-info-row"><span>{{ isAhicDashboard ? '农业生产分类二级' : '地块类型' }}</span><strong>{{ formatLandSubcatLabel(selectedVillagePlot.landSubcat) }}</strong></div>
        <div class="plot-info-row"><span>{{ isAhicDashboard ? '实测面积（亩）' : '地块面积' }}</span><strong>{{ Number(selectedVillagePlot.areaMu || 0).toFixed(2) }}{{ isAhicDashboard ? '' : ' 亩' }}</strong></div>
        <div v-if="isAhicDashboard" class="plot-info-row"><span>地块来源</span><strong>{{ formatPlotSourceTypeLabel(selectedVillagePlot.plotSourceType) }}</strong></div>
      </div>
    </div>

    <div v-if="!loading" ref="villagePlotMap" class="village-plot-map"></div>
    <div v-if="loading" class="village-map-empty">
      <div class="village-empty-state">
        <v-progress-circular
          indeterminate
          size="28"
          width="3"
          color="#67e8ff"
          class="mb-2"
        />
        <div>地块图加载中...</div>
      </div>
    </div>

    <div v-if="!selectedVillagePlot && !loading" class="plot-guide-card">
      <div class="plot-guide-title">地块分布图</div>
      <div class="plot-guide-text">{{ plotGuideText }}</div>
    </div>

  </div>
</template>

<script>
import 'ol/ol.css'
import Map from 'ol/Map'
import View from 'ol/View'
import GeoJSON from 'ol/format/GeoJSON'
import TileLayer from 'ol/layer/Tile'
import VectorLayer from 'ol/layer/Vector'
import TileWMS from 'ol/source/TileWMS'
import VectorSource from 'ol/source/Vector'
import XYZ from 'ol/source/XYZ'
import { createEmpty as createEmptyExtent, extend as extendExtent } from 'ol/extent'
import { fromLonLat } from 'ol/proj'
import { Fill, Stroke, Style } from 'ol/style'
import { INS_WMS_URL, LAND_SUB_CATEGORY_LABELS } from '@/const/const.js'
import { getDashboardPlotDetail } from '@/api/dashboard'

const DASHBOARD_PLOT_WMS_LAYER = 'ins:ins__dashboard_plot_fact'

export default {
  name: 'VillagePlotOverlay',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    village: {
      type: Object,
      default: () => ({ code: '', name: '' })
    }
  },
  computed: {
    isAhicDashboard () {
      const organize = String(this.$store?.state?.user?.organize || '').trim().toLowerCase()
      return organize === 'ahic'
    },
    dashboardPlotStyleName () {
      return this.isAhicDashboard
        ? 'ins__dashboard_plot_fact_landsubcat_ahic'
        : 'ins__dashboard_plot_fact_landsubcat'
    },
    plotSourceLegendItems () {
      return [
        {
          code: 'imported',
          label: '原始导入',
          style: {
            backgroundColor: '#2E7D32',
            borderColor: '#072716'
          }
        },
        {
          code: 'manual_drawn',
          label: '人工绘制',
          style: {
            backgroundColor: 'transparent',
            borderColor: '#FFB300',
            borderWidth: '2px'
          }
        }
      ]
    },
    legendGroups () {
      return [
        {
          code: 'A',
          label: '种植地块（A）',
          items: [
            { code: 'A01', label: this.formatLandSubcatLabel('A01'), color: '#2E7D32' },
            { code: 'A02', label: this.formatLandSubcatLabel('A02'), color: '#43A047' },
            { code: 'A03', label: this.formatLandSubcatLabel('A03'), color: '#66BB6A' },
            { code: 'A99', label: this.formatLandSubcatLabel('A99'), color: '#A5D6A7' }
          ]
        },
        {
          code: 'B',
          label: '养殖地块（B）',
          items: [
            { code: 'B01', label: this.formatLandSubcatLabel('B01'), color: '#EF6C00' },
            { code: 'B02', label: this.formatLandSubcatLabel('B02'), color: '#F4511E' },
            { code: 'B03', label: this.formatLandSubcatLabel('B03'), color: '#FB8C00' },
            { code: 'B99', label: this.formatLandSubcatLabel('B99'), color: '#8D6E63' }
          ]
        },
        {
          code: 'C',
          label: '林业地块（C）',
          items: [
            { code: 'C01', label: this.formatLandSubcatLabel('C01'), color: '#1565C0' },
            { code: 'C02', label: this.formatLandSubcatLabel('C02'), color: '#5E35B1' },
            { code: 'C99', label: this.formatLandSubcatLabel('C99'), color: '#546E7A' }
          ]
        }
      ]
    },
    plotGuideText () {
      if (this.plotDetailLoading) {
        return '正在查询地块详情...'
      }
      if (this.plotDetailError) {
        return this.plotDetailError
      }
      return '当前通过 WMS 展示村级地块，请点击任意地块后在右侧查看详情。'
    }
  },
  data () {
    return {
      villageMap: null,
      villageBoundaryLayer: null,
      plotWmsLayer: null,
      selectedVillagePlot: null,
      villageGeometryCache: {},
      locateVillageRequestId: 0,
      plotDetailLoading: false,
      plotDetailError: '',
      plotDetailRequestId: 0
    }
  },
  watch: {
    visible (value) {
      if (value) {
        this.resetDetailState()
        this.$nextTick(() => {
          this.renderVillagePlotMap()
        })
        return
      }
      this.resetDetailState()
      if (this.villageMap) {
        this.villageMap.setTarget(null)
      }
    },
    'village.code' () {
      this.resetDetailState()
      if (this.visible) {
        this.$nextTick(() => {
          this.renderVillagePlotMap()
        })
      }
    }
  },
  mounted () {
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.handleResize)
    if (this.villageMap) {
      this.villageMap.setTarget(null)
      this.villageMap = null
    }
  },
  methods: {
    resetDetailState () {
      this.plotDetailRequestId += 1
      this.locateVillageRequestId += 1
      this.selectedVillagePlot = null
      this.plotDetailLoading = false
      this.plotDetailError = ''
    },
    normalizeLandSubcat (landSubcat) {
      const normalized = String(landSubcat || '').trim().toUpperCase()
      return normalized || 'UNKNOWN'
    },
    formatLandSubcatLabel (landSubcat) {
      const normalized = this.normalizeLandSubcat(landSubcat)
      if (normalized === 'UNKNOWN') return '未分类'
      return LAND_SUB_CATEGORY_LABELS[normalized] ? `${LAND_SUB_CATEGORY_LABELS[normalized]} (${normalized})` : normalized
    },
    normalizePlotDetail (row) {
      const source = row || {}
      const areaRaw = Number(source.area_mu || 0)
      return {
        id: source.id || null,
        plotCode: source.plot_code || '--',
        plotName: source.plot_name || '',
        ownerName: source.owner_name || '--',
        ownerIdCard: source.owner_id_card || '--',
        areaMu: Number.isFinite(areaRaw) ? areaRaw : 0,
        landSubcat: source.land_sub_category || '--',
        plotSourceType: source.plot_source_type || ''
      }
    },
    formatPlotSourceTypeLabel (plotSourceType) {
      const normalized = String(plotSourceType || '').trim().toLowerCase()
      if (normalized === 'manual_drawn') return '人工绘制'
      if (normalized === 'imported') return '原始导入'
      return '--'
    },
    selectVillagePlot (row) {
      this.selectedVillagePlot = row || null
      if (!row) {
        this.plotDetailError = ''
      }
    },
    ensureVillageMap () {
      if (!this.$refs.villagePlotMap) return

      if (!this.plotWmsLayer) {
        this.plotWmsLayer = new TileLayer({
          source: new TileWMS({
            url: INS_WMS_URL,
            params: {
              LAYERS: DASHBOARD_PLOT_WMS_LAYER,
              STYLES: this.dashboardPlotStyleName,
              TILED: true,
              FORMAT: 'image/png',
              TRANSPARENT: true
            },
            crossOrigin: 'anonymous'
          }),
          opacity: 0.58,
          visible: true
        })
      }

      if (!this.villageBoundaryLayer) {
        this.villageBoundaryLayer = new VectorLayer({
          source: new VectorSource(),
          visible: this.isAhicDashboard,
          zIndex: 8,
          style: new Style({
            fill: new Fill({
              color: 'rgba(255, 179, 0, 0.08)'
            }),
            stroke: new Stroke({
              color: '#FFB300',
              width: 2.4,
              lineDash: [10, 6]
            })
          })
        })
      }

      if (!this.villageMap) {
        this.villageMap = new Map({
          target: this.$refs.villagePlotMap,
          layers: [
            new TileLayer({
              source: new XYZ({
                url: 'https://t6.tianditu.gov.cn/DataServer?T=img_w&x={x}&y={y}&l={z}&tk=2d986e569889fd35b5587da2ce9574b7',
                crossOrigin: 'anonymous'
              }),
              visible: true
            }),
            new TileLayer({
              source: new XYZ({
                url: 'https://t4.tianditu.gov.cn/DataServer?T=cia_w&tk=2d986e569889fd35b5587da2ce9574b7&x={x}&y={y}&l={z}',
                crossOrigin: 'anonymous'
              }),
              visible: true
            }),
            this.villageBoundaryLayer,
            this.plotWmsLayer
          ],
          view: new View({
            center: [13131518, 5241410],
            zoom: 16,
            minZoom: 4,
            maxZoom: 20
          }),
          controls: []
        })

        this.villageMap.on('singleclick', async evt => {
          await this.handleMapClick(evt)
        })
        return
      }

      this.villageBoundaryLayer?.setVisible(this.isAhicDashboard)
      this.villageMap.setTarget(this.$refs.villagePlotMap)
      this.villageMap.updateSize()
    },
    updateVillageBoundaryLayer (geojsonData) {
      const source = this.villageBoundaryLayer?.getSource()
      if (!source) return
      source.clear()

      if (!this.isAhicDashboard || !this.villageMap || !geojsonData) {
        this.villageBoundaryLayer.setVisible(false)
        return
      }

      const format = new GeoJSON()
      const features = format.readFeatures(geojsonData, {
        dataProjection: 'EPSG:4326',
        featureProjection: this.villageMap.getView().getProjection()
      })
      if (!features.length) {
        this.villageBoundaryLayer.setVisible(false)
        return
      }
      source.addFeatures(features)
      this.villageBoundaryLayer.setVisible(true)
    },
    getVillagePlotFilter () {
      const villageCode = String(this.village?.code || '').trim().replace(/'/g, "''")
      return villageCode ? `village_code = '${villageCode}'` : ''
    },
    updatePlotWmsFilter () {
      const source = this.plotWmsLayer?.getSource()
      const cqlFilter = this.getVillagePlotFilter()
      if (!source || !cqlFilter || typeof source.updateParams !== 'function') return
      source.updateParams({
        CQL_FILTER: cqlFilter,
        STYLES: this.dashboardPlotStyleName
      })
      source.refresh()
    },
    extractPlotIdentifiers (feature) {
      const properties = feature?.properties || {}
      const featureId = String(feature?.id || '')
      const numericFeatureId = featureId.includes('.') ? featureId.split('.').pop() : featureId
      const parsedFeatureId = Number.parseInt(numericFeatureId, 10)
      const parsedPropertyId = Number.parseInt(properties.id, 10)
      return {
        plotId: Number.isFinite(parsedPropertyId) ? parsedPropertyId : (Number.isFinite(parsedFeatureId) ? parsedFeatureId : null),
        plotCode: String(properties.dkbm || properties.plot_code || '').trim()
      }
    },
    async fetchClickedFeature (coordinate) {
      if (!this.villageMap || !this.plotWmsLayer) return null
      const source = this.plotWmsLayer.getSource()
      const view = this.villageMap.getView()
      const resolution = view.getResolution()
      const projection = view.getProjection()
      if (!source || !resolution || !projection || typeof source.getFeatureInfoUrl !== 'function') return null

      const cqlFilter = this.getVillagePlotFilter()
      const url = source.getFeatureInfoUrl(coordinate, resolution, projection, {
        INFO_FORMAT: 'application/json',
        FEATURE_COUNT: 1,
        QUERY_LAYERS: DASHBOARD_PLOT_WMS_LAYER,
        STYLES: this.dashboardPlotStyleName,
        CQL_FILTER: cqlFilter
      })
      if (!url) return null

      const response = await fetch(url)
      if (!response.ok) {
        throw new Error(`GetFeatureInfo failed: ${response.status}`)
      }
      const data = await response.json()
      return Array.isArray(data?.features) && data.features.length > 0 ? data.features[0] : null
    },
    async fetchPlotDetail (plotIdentifiers) {
      const villageCode = String(this.village?.code || '').trim()
      const params = { village_code: villageCode }
      if (plotIdentifiers.plotId) {
        params.plot_id = plotIdentifiers.plotId
      } else if (plotIdentifiers.plotCode) {
        params.plot_code = plotIdentifiers.plotCode
      } else {
        throw new Error('Missing plot identifier')
      }
      const response = await getDashboardPlotDetail(params)
      return this.normalizePlotDetail(response?.data)
    },
    async handleMapClick (evt) {
      if (!this.visible || this.loading) return
      const requestId = ++this.plotDetailRequestId
      this.plotDetailLoading = true
      this.plotDetailError = ''
      this.selectedVillagePlot = null

      try {
        const feature = await this.fetchClickedFeature(evt.coordinate)
        if (requestId !== this.plotDetailRequestId || !this.visible) return
        if (!feature) {
          this.plotDetailError = '该位置没有地块信息'
          return
        }
        const plotIdentifiers = this.extractPlotIdentifiers(feature)
        const detail = await this.fetchPlotDetail(plotIdentifiers)
        if (requestId !== this.plotDetailRequestId || !this.visible) return
        this.selectedVillagePlot = detail
      } catch (error) {
        console.error('Failed to load village plot detail:', error)
        if (requestId !== this.plotDetailRequestId || !this.visible) return
        this.plotDetailError = '获取地块详情失败'
      } finally {
        if (requestId === this.plotDetailRequestId) {
          this.plotDetailLoading = false
        }
      }
    },
    getVillageLocatePadding () {
      return [48, 320, 48, 48]
    },
    getVillageCenterCoordinates (geojsonData) {
      const feature = Array.isArray(geojsonData?.features) ? geojsonData.features[0] : null
      const properties = feature?.properties || {}
      const centerLon = Number(properties.center_lon)
      const centerLat = Number(properties.center_lat)
      if (Number.isFinite(centerLon) && Number.isFinite(centerLat)) {
        return [centerLon, centerLat]
      }
      const villageLon = Number(this.village?.lon)
      const villageLat = Number(this.village?.lat)
      if (Number.isFinite(villageLon) && Number.isFinite(villageLat)) {
        return [villageLon, villageLat]
      }
      return null
    },
    getFeatureCollectionExtent (features) {
      if (!Array.isArray(features) || features.length === 0) return null
      const extent = createEmptyExtent()
      let hasGeometry = false
      features.forEach(feature => {
        const geometry = feature?.getGeometry()
        if (!geometry) return
        extendExtent(extent, geometry.getExtent())
        hasGeometry = true
      })
      if (!hasGeometry || extent.some(value => !Number.isFinite(value))) return null
      return extent
    },
    fitToVillageGeometry (geojsonData) {
      if (!this.villageMap || !geojsonData) return false
      const format = new GeoJSON()
      const mapProjection = this.villageMap.getView().getProjection()
      const features = format.readFeatures(geojsonData, {
        dataProjection: 'EPSG:4326',
        featureProjection: mapProjection
      })
      const extent = this.getFeatureCollectionExtent(features)
      if (!extent) return false
      this.villageMap.getView().fit(extent, {
        padding: this.getVillageLocatePadding(),
        duration: 320,
        maxZoom: 17
      })
      return true
    },
    centerToVillagePoint (geojsonData) {
      if (!this.villageMap) return false
      const centerCoords = this.getVillageCenterCoordinates(geojsonData)
      if (!centerCoords) return false
      const projectedCenter = fromLonLat(centerCoords, this.villageMap.getView().getProjection())
      this.villageMap.getView().animate({
        center: projectedCenter,
        duration: 320,
        zoom: Math.max(this.villageMap.getView().getZoom() || 16, 15)
      })
      return true
    },
    async fetchVillageGeometryGeojson (villageCode) {
      const normalizedVillageCode = String(villageCode || '').trim()
      if (!normalizedVillageCode) return null
      if (this.villageGeometryCache[normalizedVillageCode]) {
        return this.villageGeometryCache[normalizedVillageCode]
      }

      const wfsUrl = 'https://geo.kungsong.com/geoserver/ks/wfs'
      const params = new URLSearchParams({
        service: 'WFS',
        version: '1.1.0',
        request: 'GetFeature',
        typeName: 'ks:geo_district_code',
        outputFormat: 'application/json',
        featureId: `geo_district_code.${normalizedVillageCode}`
      })

      try {
        const response = await fetch(`${wfsUrl}?${params.toString()}`)
        if (response.ok) {
          const data = await response.json()
          if (Array.isArray(data?.features) && data.features.length > 0) {
            this.$set(this.villageGeometryCache, normalizedVillageCode, data)
            return data
          }
        }
      } catch (error) {
        console.warn('Failed to fetch village geometry from GeoServer:', normalizedVillageCode, error)
      }

      try {
        const data = await this.$store.dispatch('fetchRegionGeometry', normalizedVillageCode)
        if (Array.isArray(data?.features) && data.features.length > 0) {
          this.$set(this.villageGeometryCache, normalizedVillageCode, data)
          return data
        }
      } catch (error) {
        console.warn('Failed to fetch village geometry from backend:', normalizedVillageCode, error)
      }

      return null
    },
    async locateToVillageBoundaryOrCenter () {
      if (!this.villageMap) return false
      const villageCode = String(this.village?.code || '').trim()
      if (!villageCode) return false

      const requestId = ++this.locateVillageRequestId
      const geojsonData = await this.fetchVillageGeometryGeojson(villageCode)
      if (!this.visible || requestId !== this.locateVillageRequestId || villageCode !== String(this.village?.code || '').trim()) {
        return false
      }

      this.updateVillageBoundaryLayer(geojsonData)
      if (this.fitToVillageGeometry(geojsonData)) {
        return true
      }
      return this.centerToVillagePoint(geojsonData)
    },
    async renderVillagePlotMap () {
      if (!this.visible || !this.$refs.villagePlotMap) return
      this.ensureVillageMap()
      this.updatePlotWmsFilter()
      this.villageMap?.updateSize()
      await this.locateToVillageBoundaryOrCenter()
    },
    handleResize () {
      if (this.villageMap && this.visible) {
        this.villageMap.updateSize()
      }
    }
  }
}
</script>

<style scoped>
.village-overlay {
  position: absolute;
  inset: 8px;
  z-index: 4;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.12);
}

.village-close-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 4;
  width: 40px;
  height: 40px;
  background: rgba(12, 138, 146, 0.65) !important;
  border: 1px solid rgba(87, 241, 255, 0.35);
  border-radius: 2px;
}

.village-map-summary {
  position: absolute;
  top: 14px;
  left: 16px;
  z-index: 4;
  min-width: 220px;
  max-width: calc(100% - 88px);
  padding: 12px 16px;
  border: 1px solid rgba(103, 232, 255, 0.24);
  background: linear-gradient(180deg, rgba(6, 36, 52, 0.82) 0%, rgba(4, 24, 36, 0.72) 100%);
  box-shadow: inset 0 0 18px rgba(50, 178, 198, 0.12);
  backdrop-filter: blur(4px);
}

.summary-title {
  color: #d7ffff;
  font-size: 18px;
  line-height: 1.3;
}

.summary-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 6px;
  color: rgba(188, 239, 255, 0.88);
  font-size: 13px;
}

.plot-side-panel {
  position: absolute;
  top: 84px;
  right: 16px;
  z-index: 4;
  width: 260px;
  max-width: calc(100% - 32px);
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.plot-card,
.plot-legend-card {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid rgba(103, 232, 255, 0.24);
  background: linear-gradient(180deg, rgba(6, 36, 52, 0.84) 0%, rgba(4, 24, 36, 0.74) 100%);
  box-shadow: inset 0 0 18px rgba(50, 178, 198, 0.12);
  backdrop-filter: blur(4px);
}

.plot-legend-title {
  margin-bottom: 8px;
  color: #89fff0;
  font-size: 14px;
  line-height: 1.3;
}

.plot-source-legend {
  margin-top: 10px;
}

.plot-source-items {
  display: grid;
  gap: 6px;
  padding: 8px;
  border: 1px solid rgba(103, 232, 255, 0.18);
}

.plot-legend-table {
  border: 1px solid rgba(103, 232, 255, 0.18);
}

.plot-legend-head,
.plot-legend-row {
  display: grid;
  grid-template-columns: 88px minmax(0, 1fr);
}

.plot-legend-head {
  background: rgba(79, 210, 221, 0.12);
}

.plot-legend-head span {
  padding: 6px 8px;
  color: #89fff0;
  font-size: 12px;
  line-height: 1.3;
}

.plot-legend-head span:first-child,
.plot-legend-primary {
  border-right: 1px solid rgba(103, 232, 255, 0.16);
}

.plot-legend-row + .plot-legend-row {
  border-top: 1px solid rgba(103, 232, 255, 0.14);
}

.plot-legend-primary {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 8px 6px;
  color: rgba(216, 255, 255, 0.96);
  font-size: 12px;
  line-height: 1.5;
}

.plot-legend-secondary {
  padding: 6px 8px;
}

.plot-legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 20px;
}

.plot-legend-item + .plot-legend-item {
  margin-top: 4px;
}

.plot-legend-swatch {
  width: 12px;
  height: 12px;
  border: 1px solid rgba(255, 255, 255, 0.45);
  border-radius: 2px;
  flex: 0 0 auto;
}

.plot-legend-label {
  color: rgba(216, 255, 255, 0.9);
  font-size: 12px;
  line-height: 1.3;
}

.village-plot-map,
.village-map-empty {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  background: rgba(4, 15, 28, 0.22);
}

.village-plot-map :deep(.ol-viewport) {
  background: rgba(4, 15, 28, 0.22);
}

.village-map-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #bcefff;
  font-size: 13px;
}

.village-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.plot-guide-card {
  position: absolute;
  left: 16px;
  bottom: 16px;
  z-index: 3;
  width: 280px;
  max-width: calc(100% - 32px);
  padding: 12px 14px;
  border: 1px solid rgba(103, 232, 255, 0.18);
  background: rgba(6, 34, 52, 0.72);
  color: #d8ffff;
  backdrop-filter: blur(4px);
}

.plot-guide-title {
  margin-bottom: 6px;
  font-size: 16px;
  color: #89fff0;
}

.plot-guide-text {
  color: rgba(216, 255, 255, 0.88);
  font-size: 13px;
  line-height: 1.5;
}

.plot-info-panel {
  position: relative;
  z-index: 4;
}

.plot-info-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.plot-info-title {
  font-size: 14px;
  color: #89fff0;
  line-height: 1.3;
}

.plot-info-action {
  min-width: auto !important;
  padding: 0 !important;
}

.plot-info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  min-height: 20px;
  padding: 0;
  border-bottom: 1px solid rgba(103, 232, 255, 0.1);
  color: #d8ffff;
  font-size: 12px;
}

.plot-info-row span {
  color: rgba(201, 255, 246, 0.82);
  white-space: nowrap;
}

.plot-info-row strong {
  color: #ffffff;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.plot-info-row + .plot-info-row {
  margin-top: 6px;
}

@media (max-width: 1264px) {
  .village-map-summary {
    top: 12px;
    left: 12px;
    min-width: 0;
    max-width: calc(100% - 72px);
    padding: 10px 12px;
  }

  .summary-title {
    font-size: 16px;
  }

  .summary-meta {
    font-size: 12px;
  }

  .plot-side-panel {
    top: 86px;
    right: 12px;
    width: min(260px, calc(100% - 24px));
  }

  .plot-card,
  .plot-legend-card {
    padding: 8px 10px;
  }

  .plot-guide-card {
    left: 12px;
    bottom: 12px;
    width: min(280px, calc(100% - 24px));
    padding: 10px 12px;
  }

  .plot-info-panel {
    width: 100%;
  }
}
</style>
